import React, { useEffect, useState } from "react";
import { Gift, Copy, Check, Share2, Users2, Coins } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/referrals/me");
        setData(r.data);
      } catch { /* silent */ }
    })();
  }, []);

  if (!data) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  const shareNative = async () => {
    if (!navigator.share) {
      copy();
      return;
    }
    try {
      await navigator.share({
        title: "Join me on BroadcastFlow AI",
        text: `Use my invite link and we'll both get ${data.bonus_per_referral} bonus credits to try AI tools for radio stations.`,
        url: data.share_url,
      });
    } catch { /* user cancelled — ignore */ }
  };

  return (
    <div
      className="rounded-xl border border-brand-amber/30 bg-gradient-to-br from-brand-amber/5 to-brand-cyan/5 p-6"
      data-testid="referral-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Gift className="h-5 w-5 text-brand-amber" />
            <h3 className="font-display text-xl font-bold">Invite a friend, earn credits</h3>
          </div>
          <p className="text-sm text-ink-secondary">
            Share your link — when they sign up you'll <strong className="text-brand-amber">both</strong> get <strong className="text-brand-amber">{data.bonus_per_referral} bonus credits</strong>. No limit.
          </p>
        </div>
      </div>

      {/* Share link box */}
      <div className="flex flex-wrap items-stretch gap-2 mb-4">
        <input
          readOnly
          value={data.share_url}
          onFocus={(e) => e.target.select()}
          data-testid="referral-share-url"
          className="flex-1 min-w-[160px] px-3 py-2 rounded-md border border-line bg-bg-elevated font-mono text-xs text-ink-secondary truncate outline-none focus:border-brand-amber"
        />
        <button
          onClick={copy}
          data-testid="referral-copy-btn"
          className="inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-3 py-2 text-xs font-semibold hover:bg-brand-amber/20 transition"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          onClick={shareNative}
          data-testid="referral-share-btn"
          className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 text-xs font-semibold hover:text-brand-amber transition"
        >
          <Share2 className="h-3.5 w-3.5" /> Share
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <Tile icon={Users2} label="Referred" value={data.referred_count} testid="referral-count" />
        <Tile icon={Coins} label="Credits earned" value={data.credits_earned} testid="referral-credits-earned" />
      </div>

      {/* Recent referrals */}
      {data.recent?.length > 0 && (
        <div className="border-t border-line/30 pt-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-2">Recent invites</div>
          <ul className="space-y-1">
            {data.recent.map((r, i) => (
              <li key={i} className="flex items-center justify-between text-xs">
                <span className="text-ink-secondary font-mono">{r.referred_email_partial}</span>
                <span className="text-brand-amber font-mono">+{r.credits_granted}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
        Your code: <span className="text-brand-amber" data-testid="referral-code">{data.code}</span>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-lg border border-line bg-bg-surface/60 p-3" data-testid={testid}>
      <div className="flex items-center gap-1 mb-1">
        <Icon className="h-3 w-3 text-brand-cyan" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{label}</span>
      </div>
      <div className="font-display text-2xl font-bold text-brand-amber">{value}</div>
    </div>
  );
}
